/**
 * Zendesk Support Report Page
 *
 * Displays support ticket analytics - volumes, CSAT, response times, channels
 */

import { parseISO, format } from 'date-fns'
import { useState, useEffect, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line,
  ComposedChart, Area,
} from 'recharts'
import { zendeskAPI } from '../services/api'
import { useAuthStore } from '../stores/authStore'
import { DateRangePicker } from '../components/DateRangePicker'
import AggregateZendeskView from '../components/AggregateZendeskView'
import DataFreshnessWarning from '../components/DataFreshnessWarning'
import RepPerformance from '../components/RepPerformance'
import P90TailSample from '../components/P90TailSample'
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
  Button, Badge,
} from '../components/ui'

// Format numbers
function formatNumber(value) {
  if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`
  if (value >= 1000) return `${(value / 1000).toFixed(1)}K`
  return Math.round(value).toLocaleString()
}

// Format time in minutes to human readable
function formatMinutes(mins) {
  if (mins == null) return '--'
  if (mins < 60) return `${Math.round(mins)}m`
  if (mins < 1440) return `${(mins / 60).toFixed(1)}h`
  return `${(mins / 1440).toFixed(1)}d`
}

// Format hours to human readable
function formatHours(hrs) {
  if (hrs == null) return '--'
  if (hrs < 1) return `${Math.round(hrs * 60)}m`
  if (hrs < 24) return `${hrs.toFixed(1)}h`
  return `${(hrs / 24).toFixed(1)}d`
}

// CSAT score display
function formatCSAT(score) {
  if (score == null) return '--'
  return `${(score * 100).toFixed(0)}%`
}

// Pie chart custom label
function renderPieLabel({ cx, cy, midAngle, innerRadius, outerRadius, percent }) {
  // Recharts passes `percent` as a 0-1 fraction, but it ALSO spreads the datum
  // into these props, so a data key named `percent` silently replaces it. This
  // page's data uses `pct` and is safe today; the aggregate view used `percent`
  // and rendered 45% as 4520%. Anything above 1 is already a percentage.
  const fraction = percent > 1 ? percent / 100 : percent
  if (!(fraction >= 0.05)) return null
  const RADIAN = Math.PI / 180
  const radius = innerRadius + (outerRadius - innerRadius) * 0.5
  const x = cx + radius * Math.cos(-midAngle * RADIAN)
  const y = cy + radius * Math.sin(-midAngle * RADIAN)

  return (
    <text x={x} y={y} fill="white" textAnchor="middle" dominantBaseline="central"
      className="text-xs font-bold" style={{ textShadow: '0 1px 2px rgba(0,0,0,0.5)' }}>
      {`${(fraction * 100).toFixed(0)}%`}
    </text>
  )
}

// Custom tooltip
function ChartTooltip({ active, payload, label, valueFormatter }) {
  if (active && payload && payload.length) {
    return (
      <div className="bg-popover border border-border rounded-lg shadow-lg p-3 text-sm">
        <p className="font-medium text-popover-foreground mb-2">{label}</p>
        {payload.map((entry, index) => (
          <p key={index} style={{ color: entry.color || entry.stroke }} className="flex justify-between gap-4">
            <span>{entry.name}:</span>
            <span className="font-medium">
              {valueFormatter ? valueFormatter(entry.value, entry.name) : entry.value}
            </span>
          </p>
        ))}
      </div>
    )
  }
  return null
}

// Pie tooltip
function PieTooltip({ active, payload }) {
  if (active && payload && payload.length) {
    const data = payload[0]
    return (
      <div className="bg-popover text-popover-foreground border border-border rounded-lg shadow-xl p-3 text-sm z-50 relative">
        <div className="flex items-center gap-2 mb-1">
          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: data.payload.color }} />
          <span className="font-semibold">{data.name}</span>
        </div>
        <div className="text-lg font-bold">{data.value.toLocaleString()} tickets</div>
        <div className="text-xs text-muted-foreground">{data.payload.pct?.toFixed(1)}%</div>
      </div>
    )
  }
  return null
}

// Quick date range presets shown as pill buttons
const DATE_PRESETS = [
  { label: '7D', days: 6 },
  { label: '30D', days: 29 },
  { label: '90D', days: 89 },
  { label: 'MTD', getRange: () => {
    const now = new Date()
    return { from: new Date(now.getFullYear(), now.getMonth(), 1), to: now }
  }},
  { label: 'YTD', getRange: () => {
    const now = new Date()
    return { from: new Date(now.getFullYear(), 0, 1), to: now }
  }},
]

function getPresetRange(preset) {
  if (preset.getRange) return preset.getRange()
  const end = new Date()
  const start = new Date()
  start.setDate(start.getDate() - preset.days)
  return { from: start, to: end }
}

function getActivePreset(dateRange) {
  if (!dateRange?.from || !dateRange?.to) return null
  const now = new Date()
  const toDay = dateRange.to.toDateString()
  const fromDay = dateRange.from.toDateString()
  if (toDay !== now.toDateString()) return null

  for (const preset of DATE_PRESETS) {
    const range = getPresetRange(preset)
    if (range.from.toDateString() === fromDay) return preset.label
  }
  return null
}

/**
 * Phentermine against everything else, on one screen.
 *
 * Every figure is recomputed from ticket rows rather than the daily rollup,
 * which carries no tags — so "All" here is the SUM of the two columns and
 * reproduces the rollup exactly. See services/zendeskSegments.
 *
 * `All` is rendered last and muted: it is the number the rest of the page
 * already shows, kept only so the two segments visibly add up to something
 * familiar rather than floating free.
 */
function SegmentComparison({ segments }) {
  const rows = [
    { key: 'tickets_created', label: 'Tickets created', fmt: formatNumber },
    { key: 'tickets_solved', label: 'Tickets solved', fmt: formatNumber },
    { key: 'csat_score', label: 'CSAT', fmt: (v) => (v == null ? '—' : `${(v * 100).toFixed(1)}%`) },
    { key: 'csat_offered', label: 'CSAT responses', fmt: formatNumber },
    { key: 'median_first_reply_time_minutes', label: 'Median first reply', fmt: formatMinutes },
    { key: 'median_solve_time_hours', label: 'Median solve', fmt: formatHours },
    { key: 'p90_solve_time_hours', label: 'P90 solve', fmt: formatHours },
  ]
  const cols = [...segments.segments, segments.all]

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Phentermine vs everything else</CardTitle>
        <CardDescription>
          Phentermine is the majority of this brand's support load, so the figures above
          are largely its figures. These are computed from the tickets themselves.
        </CardDescription>
      </CardHeader>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-xs text-muted-foreground">
                <th className="px-4 py-2 font-medium" />
                {cols.map(c => (
                  <th
                    key={c.key}
                    className={`px-4 py-2 text-right font-medium whitespace-nowrap ${
                      c.key === 'all' ? 'text-muted-foreground/70' : ''
                    }`}
                  >
                    {c.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.key} className="border-b last:border-0 hover:bg-muted/40">
                  <td className="px-4 py-2 text-muted-foreground whitespace-nowrap">{r.label}</td>
                  {cols.map(c => (
                    <td
                      key={c.key}
                      className={`px-4 py-2 text-right tabular-nums whitespace-nowrap ${
                        c.key === 'all' ? 'text-muted-foreground/70' : 'font-medium'
                      }`}
                    >
                      {r.fmt(c[r.key])}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="px-4 py-3 text-xs text-muted-foreground">
          A ticket counts as phentermine when it carries the <code>phentermine</code> tag.
          Created and solved reproduce the daily rollup exactly; the two segments sum to All.
        </p>
      </CardContent>
    </Card>
  )
}

export default function ZendeskPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const { currentClinicId } = useAuthStore()

  // Date range state
  const [dateRange, setDateRange] = useState(() => {
    const startParam = searchParams.get('startDate')
    const endParam = searchParams.get('endDate')

    if (startParam && endParam) {
      return { from: parseISO(startParam), to: parseISO(endParam) }
    }

    const end = new Date()
    const start = new Date()
    start.setDate(start.getDate() - 29)
    return { from: start, to: end }
  })

  const [data, setData] = useState(null)
  const [weeklyData, setWeeklyData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const isAggregateMode = currentClinicId === null

  const fetchData = useCallback(async () => {
    if (!dateRange?.from || !dateRange?.to) return
    if (!isAggregateMode && !currentClinicId) return

    setLoading(true)
    setError(null)

    try {
      const params = {
        startDate: format(dateRange.from, 'yyyy-MM-dd'),
        endDate: format(dateRange.to, 'yyyy-MM-dd'),
      }

      if (isAggregateMode) {
        const [reportRes, weeklyRes] = await Promise.all([
          zendeskAPI.getReportAggregate(params),
          zendeskAPI.getWeeklyAggregate({ weeks: 12 }),
        ])
        setData(reportRes.data)
        setWeeklyData(weeklyRes.data.weeks || [])
      } else {
        const [reportRes, weeklyRes] = await Promise.all([
          zendeskAPI.getReport(params),
          zendeskAPI.getWeekly({ weeks: 12 }),
        ])
        setData(reportRes.data)
        setWeeklyData(weeklyRes.data.weeks || [])
      }
    } catch (err) {
      console.error('Error fetching Zendesk data:', err)
      setError(err.response?.data?.error?.message || err.response?.data?.error || 'Failed to fetch support data')
    } finally {
      setLoading(false)
    }
  }, [currentClinicId, isAggregateMode, dateRange])

  // Clear data when switching modes
  useEffect(() => {
    setData(null)
    setWeeklyData(null)
  }, [currentClinicId])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  // Update URL params when date range changes
  useEffect(() => {
    if (dateRange?.from && dateRange?.to) {
      const params = new URLSearchParams(searchParams)
      params.set('startDate', format(dateRange.from, 'yyyy-MM-dd'))
      params.set('endDate', format(dateRange.to, 'yyyy-MM-dd'))
      setSearchParams(params, { replace: true })
    }
  }, [dateRange])

  const activePreset = getActivePreset(dateRange)

  // Aggregate mode
  if (isAggregateMode) {
    return (
      <div className="container mx-auto px-4 py-6 space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-foreground">Support</h1>
            <p className="text-muted-foreground mt-1">Zendesk analytics - All Brands</p>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center rounded-lg border border-border overflow-hidden">
              {DATE_PRESETS.map(preset => (
                <button
                  key={preset.label}
                  onClick={() => setDateRange(getPresetRange(preset))}
                  className={`px-3 py-1.5 text-xs font-medium transition-colors border-r border-border last:border-r-0 ${
                    activePreset === preset.label
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-background hover:bg-muted text-muted-foreground'
                  }`}
                >
                  {preset.label}
                </button>
              ))}
            </div>
            <DateRangePicker dateRange={dateRange} onDateRangeChange={setDateRange} />
            <Button variant="outline" size="sm" onClick={fetchData} disabled={loading}>
              {loading ? 'Loading...' : 'Refresh'}
            </Button>
          </div>
        </div>

        {error && (
          <Card className="border-destructive">
            <CardContent className="pt-6">
              <p className="text-destructive">{typeof error === 'string' ? error : error.message || 'Failed to fetch data'}</p>
            </CardContent>
          </Card>
        )}

        <DataFreshnessWarning
          daily={data?.daily}
          dateField="date"
          dateRange={dateRange}
          loading={loading}
        />

        <AggregateZendeskView
          data={data}
          weeklyData={weeklyData}
          loading={loading}
          error={error}
          dateRange={dateRange}
        />

        {!error && <P90TailSample dateRange={dateRange} aggregate />}
      </div>
    )
  }

  if (!currentClinicId) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Card>
          <CardHeader>
            <CardTitle>Clinic Selection Required</CardTitle>
            <CardDescription>
              Please select a clinic from the header dropdown to view support data.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    )
  }

  // Prepare chart data
  const dailyData = data?.daily || []
  const channelPieData = data?.by_channel?.map(ch => ({
    name: ch.name,
    value: ch.count,
    color: ch.color,
    pct: ch.percent,
  })) || []
  const statusData = data?.by_status || []

  // CSAT trend data
  const csatTrendData = dailyData.filter(d => d.csat_offered > 0).map(d => ({
    date: d.displayDate,
    score: d.csat_score != null ? Math.round(d.csat_score * 100) : null,
    good: d.csat_good,
    bad: d.csat_bad,
  }))

  // Response time trend
  const responseTimeTrend = dailyData.filter(d =>
    d.median_first_reply_time_minutes != null || d.median_solve_time_hours != null
  ).map(d => ({
    date: d.displayDate,
    first_reply_min: d.median_first_reply_time_minutes,
    solve_time_hrs: d.median_solve_time_hours,
    p90_reply_min: d.p90_first_reply_time_minutes,
  }))

  // Weekly data for WoW charts
  const weeks = weeklyData || []
  const weeklyVolumeData = weeks.map(w => ({
    label: w.label,
    created: w.tickets_created,
    solved: w.tickets_solved,
    change: w.wow_volume_change,
    changePct: w.wow_volume_change_pct,
  }))
  const weeklyCSATData = weeks.map(w => ({
    label: w.label,
    csat: w.csat_score != null ? Math.round(w.csat_score * 100) : null,
    good: w.csat_good,
    bad: w.csat_bad,
    change: w.wow_csat_change != null ? Math.round(w.wow_csat_change * 100) : null,
  }))
  const weeklyP90ReplyData = weeks.map(w => ({
    label: w.label,
    p90_minutes: w.p90_first_reply_minutes != null ? Math.round(w.p90_first_reply_minutes) : null,
    change: w.wow_p90_reply_change != null ? Math.round(w.wow_p90_reply_change) : null,
  }))
  const weeklyP90SolveData = weeks.map(w => ({
    label: w.label,
    p90_hours: w.p90_solve_time_hours != null ? parseFloat(w.p90_solve_time_hours.toFixed(1)) : null,
    change: w.wow_p90_solve_change != null ? parseFloat(w.wow_p90_solve_change.toFixed(1)) : null,
  }))

  return (
    <div className="container mx-auto px-4 py-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground">Support</h1>
          <p className="text-muted-foreground mt-1">
            Zendesk ticket analytics{data?.brand_name ? ` - ${data.brand_name}` : ''}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center rounded-lg border border-border overflow-hidden">
            {DATE_PRESETS.map(preset => (
              <button
                key={preset.label}
                onClick={() => setDateRange(getPresetRange(preset))}
                className={`px-3 py-1.5 text-xs font-medium transition-colors border-r border-border last:border-r-0 ${
                  activePreset === preset.label
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-background hover:bg-muted text-muted-foreground'
                }`}
              >
                {preset.label}
              </button>
            ))}
          </div>
          <DateRangePicker dateRange={dateRange} onDateRangeChange={setDateRange} />
          <Button variant="outline" size="sm" onClick={fetchData} disabled={loading}>
            {loading ? 'Loading...' : 'Refresh'}
          </Button>
        </div>
      </div>

      {error && (
        <Card className="border-destructive">
          <CardContent className="pt-6">
            <p className="text-destructive">{error}</p>
          </CardContent>
        </Card>
      )}

      <DataFreshnessWarning
        daily={data?.daily}
        dateField="date"
        dateRange={dateRange}
        loading={loading}
      />

      {data?.brand_not_supported && (
        <Card>
          <CardContent className="pt-6">
            <p className="text-muted-foreground">Zendesk data is not available for this clinic.</p>
          </CardContent>
        </Card>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4" />
            <p className="text-muted-foreground">Loading support data...</p>
          </div>
        </div>
      ) : data && !data.brand_not_supported && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Tickets Created
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{formatNumber(data.summary.tickets_created)}</div>
                <p className="text-xs text-muted-foreground">{data.date_range?.days} days</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Tickets Solved
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{formatNumber(data.summary.tickets_solved)}</div>
                <p className="text-xs text-muted-foreground">
                  {data.summary.tickets_created > 0
                    ? `${((data.summary.tickets_solved / data.summary.tickets_created) * 100).toFixed(0)}% solve rate`
                    : '--'}
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  CSAT Score
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className={`text-2xl font-bold ${
                  data.summary.csat_score != null
                    ? data.summary.csat_score >= 0.8 ? 'text-green-600 dark:text-green-400'
                      : data.summary.csat_score >= 0.6 ? 'text-yellow-600 dark:text-yellow-400'
                      : 'text-red-600 dark:text-red-400'
                    : ''
                }`}>
                  {formatCSAT(data.summary.csat_score)}
                </div>
                <p className="text-xs text-muted-foreground">
                  {data.summary.csat_good} good / {data.summary.csat_offered} offered
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Avg First Reply
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {formatMinutes(data.summary.avg_first_reply_minutes)}
                </div>
                <p className="text-xs text-muted-foreground">Median response time</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Avg Solve Time
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {formatHours(data.summary.avg_solve_time_hours)}
                </div>
                <p className="text-xs text-muted-foreground">Median resolution</p>
              </CardContent>
            </Card>
          </div>

          {/* Phentermine split — TMates only. Side by side rather than a filter:
              the whole point is the comparison, and a toggle makes you hold one
              number in your head while you fetch the other. */}
          {data.segments && <SegmentComparison segments={data.segments} />}

          {/* Ticket Volume Chart */}
          {dailyData.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Ticket Volume</CardTitle>
                <CardDescription>Daily tickets created vs solved</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={dailyData}>
                      <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                      <XAxis dataKey="displayDate" tick={{ fontSize: 11 }} tickLine={false} />
                      <YAxis tick={{ fontSize: 12 }} tickLine={false} />
                      <Tooltip content={<ChartTooltip />} />
                      <Legend />
                      <Bar dataKey="tickets_created" name="Created" fill="#3B82F6" radius={[2, 2, 0, 0]} />
                      <Bar dataKey="tickets_solved" name="Solved" fill="#22C55E" radius={[2, 2, 0, 0]} />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Week over Week Section */}
          {weeks.length > 1 && (
            <>
              <div className="pt-2">
                <h2 className="text-lg font-semibold text-foreground">Week over Week</h2>
                <p className="text-sm text-muted-foreground">Trends across the last {weeks.length} weeks</p>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Weekly Ticket Volume */}
                <Card>
                  <CardHeader>
                    <CardTitle>Ticket Volume by Week</CardTitle>
                    <CardDescription>Created and solved tickets per week</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={weeklyVolumeData}>
                          <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                          <XAxis dataKey="label" tick={{ fontSize: 11 }} tickLine={false} />
                          <YAxis tick={{ fontSize: 12 }} tickLine={false} />
                          <Tooltip content={<ChartTooltip valueFormatter={(v, name) => {
                            if (name === 'WoW Change') return `${v > 0 ? '+' : ''}${v}`
                            return v.toLocaleString()
                          }} />} />
                          <Legend />
                          <Bar dataKey="created" name="Created" fill="#3B82F6" radius={[2, 2, 0, 0]} />
                          <Bar dataKey="solved" name="Solved" fill="#22C55E" radius={[2, 2, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                    {/* WoW change indicators */}
                    <div className="flex gap-2 mt-3 flex-wrap">
                      {weeklyVolumeData.slice(-4).map((w, i) => (
                        w.changePct != null && (
                          <div key={i} className="flex items-center gap-1 text-xs">
                            <span className="text-muted-foreground">{w.label}:</span>
                            <span className={w.changePct > 0 ? 'text-red-500' : w.changePct < 0 ? 'text-green-500' : 'text-muted-foreground'}>
                              {w.changePct > 0 ? '+' : ''}{w.changePct.toFixed(0)}%
                            </span>
                          </div>
                        )
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Weekly CSAT */}
                <Card>
                  <CardHeader>
                    <CardTitle>CSAT by Week</CardTitle>
                    <CardDescription>Customer satisfaction score trend</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <ComposedChart data={weeklyCSATData}>
                          <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                          <XAxis dataKey="label" tick={{ fontSize: 11 }} tickLine={false} />
                          <YAxis
                            yAxisId="left"
                            domain={[0, 100]}
                            tick={{ fontSize: 12 }}
                            tickLine={false}
                            tickFormatter={(v) => `${v}%`}
                          />
                          <YAxis
                            yAxisId="right"
                            orientation="right"
                            tick={{ fontSize: 12 }}
                            tickLine={false}
                          />
                          <Tooltip content={<ChartTooltip valueFormatter={(v, name) => {
                            if (name === 'CSAT %') return v != null ? `${v}%` : '--'
                            return v
                          }} />} />
                          <Legend />
                          <Bar yAxisId="right" dataKey="good" name="Good" fill="#22C55E" opacity={0.5} radius={[2, 2, 0, 0]} />
                          <Bar yAxisId="right" dataKey="bad" name="Bad" fill="#EF4444" opacity={0.5} radius={[2, 2, 0, 0]} />
                          <Line
                            yAxisId="left"
                            type="monotone"
                            dataKey="csat"
                            name="CSAT %"
                            stroke="#22C55E"
                            strokeWidth={2.5}
                            dot={{ r: 4, fill: '#22C55E' }}
                            connectNulls
                          />
                        </ComposedChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="flex gap-2 mt-3 flex-wrap">
                      {weeklyCSATData.slice(-4).map((w, i) => (
                        w.change != null && (
                          <div key={i} className="flex items-center gap-1 text-xs">
                            <span className="text-muted-foreground">{w.label}:</span>
                            <span className={w.change > 0 ? 'text-green-500' : w.change < 0 ? 'text-red-500' : 'text-muted-foreground'}>
                              {w.change > 0 ? '+' : ''}{w.change}pp
                            </span>
                          </div>
                        )
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Weekly P90 First Response */}
                <Card>
                  <CardHeader>
                    <CardTitle>P90 First Response by Week</CardTitle>
                    <CardDescription>90th percentile first reply time (minutes)</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <ComposedChart data={weeklyP90ReplyData}>
                          <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                          <XAxis dataKey="label" tick={{ fontSize: 11 }} tickLine={false} />
                          <YAxis tick={{ fontSize: 12 }} tickLine={false} tickFormatter={(v) => formatMinutes(v)} />
                          <Tooltip content={<ChartTooltip valueFormatter={(v) => formatMinutes(v)} />} />
                          <Legend />
                          <Area
                            type="monotone"
                            dataKey="p90_minutes"
                            name="P90 First Reply"
                            stroke="#3B82F6"
                            fill="#3B82F6"
                            fillOpacity={0.1}
                            strokeWidth={2}
                            connectNulls
                          />
                          <Line
                            type="monotone"
                            dataKey="p90_minutes"
                            name="P90 First Reply"
                            stroke="#3B82F6"
                            strokeWidth={2.5}
                            dot={{ r: 4, fill: '#3B82F6' }}
                            connectNulls
                            legendType="none"
                          />
                        </ComposedChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="flex gap-2 mt-3 flex-wrap">
                      {weeklyP90ReplyData.slice(-4).map((w, i) => (
                        w.change != null && (
                          <div key={i} className="flex items-center gap-1 text-xs">
                            <span className="text-muted-foreground">{w.label}:</span>
                            <span className={w.change < 0 ? 'text-green-500' : w.change > 0 ? 'text-red-500' : 'text-muted-foreground'}>
                              {w.change > 0 ? '+' : ''}{formatMinutes(w.change)}
                            </span>
                          </div>
                        )
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Weekly P90 Solve Time */}
                <Card>
                  <CardHeader>
                    <CardTitle>P90 Solve Time by Week</CardTitle>
                    <CardDescription>90th percentile resolution time (hours)</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <ComposedChart data={weeklyP90SolveData}>
                          <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                          <XAxis dataKey="label" tick={{ fontSize: 11 }} tickLine={false} />
                          <YAxis tick={{ fontSize: 12 }} tickLine={false} tickFormatter={(v) => formatHours(v)} />
                          <Tooltip content={<ChartTooltip valueFormatter={(v) => formatHours(v)} />} />
                          <Legend />
                          <Area
                            type="monotone"
                            dataKey="p90_hours"
                            name="P90 Solve Time"
                            stroke="#F59E0B"
                            fill="#F59E0B"
                            fillOpacity={0.1}
                            strokeWidth={2}
                            connectNulls
                          />
                          <Line
                            type="monotone"
                            dataKey="p90_hours"
                            name="P90 Solve Time"
                            stroke="#F59E0B"
                            strokeWidth={2.5}
                            dot={{ r: 4, fill: '#F59E0B' }}
                            connectNulls
                            legendType="none"
                          />
                        </ComposedChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="flex gap-2 mt-3 flex-wrap">
                      {weeklyP90SolveData.slice(-4).map((w, i) => (
                        w.change != null && (
                          <div key={i} className="flex items-center gap-1 text-xs">
                            <span className="text-muted-foreground">{w.label}:</span>
                            <span className={w.change < 0 ? 'text-green-500' : w.change > 0 ? 'text-red-500' : 'text-muted-foreground'}>
                              {w.change > 0 ? '+' : ''}{formatHours(w.change)}
                            </span>
                          </div>
                        )
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </>
          )}

          <P90TailSample dateRange={dateRange} scopeKey={currentClinicId} />

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Channel Breakdown */}
            <Card>
              <CardHeader>
                <CardTitle>Tickets by Channel</CardTitle>
                <CardDescription>How tickets are submitted</CardDescription>
              </CardHeader>
              <CardContent>
                {channelPieData.length > 0 ? (
                  <div className="h-72 relative">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={channelPieData}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={renderPieLabel}
                          innerRadius={55}
                          outerRadius={100}
                          paddingAngle={2}
                          dataKey="value"
                        >
                          {channelPieData.map((entry, index) => (
                            <Cell key={index} fill={entry.color} stroke="hsl(var(--background))" strokeWidth={2} />
                          ))}
                        </Pie>
                        <Tooltip content={<PieTooltip />} wrapperStyle={{ zIndex: 100 }} />
                        <Legend
                          verticalAlign="bottom"
                          height={36}
                          formatter={(value) => <span className="text-foreground text-sm">{value}</span>}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-0" style={{ marginBottom: '36px' }}>
                      <div className="text-center">
                        <div className="text-xs text-muted-foreground uppercase tracking-wider">Total</div>
                        <div className="text-lg font-bold text-foreground">
                          {channelPieData.reduce((sum, d) => sum + d.value, 0).toLocaleString()}
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="h-72 flex items-center justify-center text-muted-foreground">
                    No channel data available
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Status Distribution */}
            <Card>
              <CardHeader>
                <CardTitle>Ticket Status</CardTitle>
                <CardDescription>Current status distribution for period</CardDescription>
              </CardHeader>
              <CardContent>
                {statusData.length > 0 ? (
                  <div className="space-y-3">
                    {statusData.map(s => {
                      const totalStatusTickets = statusData.reduce((sum, st) => sum + st.count, 0)
                      const pct = totalStatusTickets > 0 ? (s.count / totalStatusTickets) * 100 : 0
                      return (
                        <div key={s.status}>
                          <div className="flex items-center justify-between mb-1">
                            <div className="flex items-center gap-2">
                              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: s.color }} />
                              <span className="text-sm font-medium">{s.name}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-bold">{s.count.toLocaleString()}</span>
                              <Badge variant="secondary" className="text-xs">{pct.toFixed(1)}%</Badge>
                            </div>
                          </div>
                          <div className="w-full bg-muted rounded-full h-2">
                            <div
                              className="h-2 rounded-full transition-all"
                              style={{ width: `${pct}%`, backgroundColor: s.color }}
                            />
                          </div>
                        </div>
                      )
                    })}
                  </div>
                ) : (
                  <div className="h-72 flex items-center justify-center text-muted-foreground">
                    No status data available
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* CSAT Trend */}
          {csatTrendData.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>CSAT Trend</CardTitle>
                <CardDescription>Customer satisfaction score over time</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={csatTrendData}>
                      <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                      <XAxis dataKey="date" tick={{ fontSize: 11 }} tickLine={false} />
                      <YAxis
                        domain={[0, 100]}
                        tick={{ fontSize: 12 }}
                        tickLine={false}
                        tickFormatter={(v) => `${v}%`}
                      />
                      <Tooltip
                        content={
                          <ChartTooltip valueFormatter={(v, name) =>
                            name === 'CSAT %' ? `${v}%` : v
                          } />
                        }
                      />
                      <Legend />
                      <Area
                        type="monotone"
                        dataKey="score"
                        name="CSAT %"
                        stroke="#22C55E"
                        fill="#22C55E"
                        fillOpacity={0.1}
                        strokeWidth={2}
                        dot={{ r: 3 }}
                        connectNulls
                      />
                      <Bar dataKey="good" name="Good" fill="#22C55E" opacity={0.4} />
                      <Bar dataKey="bad" name="Bad" fill="#EF4444" opacity={0.4} />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Response Time Trend */}
          {responseTimeTrend.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Response Times</CardTitle>
                <CardDescription>Median first reply time (minutes) and solve time (hours)</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={responseTimeTrend}>
                      <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                      <XAxis dataKey="date" tick={{ fontSize: 11 }} tickLine={false} />
                      <YAxis
                        yAxisId="left"
                        tick={{ fontSize: 12 }}
                        tickLine={false}
                        label={{ value: 'Reply (min)', angle: -90, position: 'insideLeft', style: { fontSize: 11, fill: 'hsl(var(--muted-foreground))' } }}
                      />
                      <YAxis
                        yAxisId="right"
                        orientation="right"
                        tick={{ fontSize: 12 }}
                        tickLine={false}
                        label={{ value: 'Solve (hrs)', angle: 90, position: 'insideRight', style: { fontSize: 11, fill: 'hsl(var(--muted-foreground))' } }}
                      />
                      <Tooltip
                        content={
                          <ChartTooltip valueFormatter={(v, name) => {
                            if (name.includes('Reply')) return formatMinutes(v)
                            if (name.includes('Solve')) return formatHours(v)
                            return v
                          }} />
                        }
                      />
                      <Legend />
                      <Line
                        yAxisId="left"
                        type="monotone"
                        dataKey="first_reply_min"
                        name="Median First Reply (min)"
                        stroke="#3B82F6"
                        strokeWidth={2}
                        dot={{ r: 2 }}
                        connectNulls
                      />
                      <Line
                        yAxisId="left"
                        type="monotone"
                        dataKey="p90_reply_min"
                        name="P90 First Reply (min)"
                        stroke="#93C5FD"
                        strokeWidth={1}
                        strokeDasharray="4 4"
                        dot={false}
                        connectNulls
                      />
                      <Line
                        yAxisId="right"
                        type="monotone"
                        dataKey="solve_time_hrs"
                        name="Median Solve Time (hrs)"
                        stroke="#F59E0B"
                        strokeWidth={2}
                        dot={{ r: 2 }}
                        connectNulls
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          )}

          {/* CSAT Feedback - all DSATs + recent good */}
          {(() => {
            // DSATs: show all from the period (up to 100, deduped per ticket)
            const allDsats = data.csat?.dsats || (data.csat?.recent_comments || []).filter(c => c.score === 'bad')
            const goodComments = (data.csat?.recent_comments || []).filter(c =>
              c.score === 'good' &&
              ((c.reason && c.reason !== 'No reason provided') || (c.comment && c.comment.trim() !== ''))
            )

            if (allDsats.length === 0 && goodComments.length === 0) return null

            return (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* All DSATs */}
                <Card className="border-red-200 dark:border-red-900/50">
                  <CardHeader>
                    <div className="flex items-center gap-2">
                      <Badge variant="destructive">Bad</Badge>
                      <CardTitle className="text-base">All Negative Ratings</CardTitle>
                    </div>
                    <CardDescription>
                      {allDsats.length} DSAT{allDsats.length !== 1 ? 's' : ''} in this period (deduped per ticket)
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {allDsats.length > 0 ? (
                      <div className="space-y-3 max-h-[600px] overflow-y-auto">
                        {allDsats.map((c, i) => {
                          const hasReason = c.reason && c.reason !== 'No reason provided'
                          const hasComment = c.comment && c.comment.trim() !== ''
                          return (
                            <div key={i} className="border border-red-200 dark:border-red-900/40 bg-red-50/50 dark:bg-red-950/20 rounded-lg p-3">
                              <div className="flex items-center justify-between mb-1.5">
                                {hasReason && <span className="text-xs font-medium text-red-700 dark:text-red-400">{c.reason}</span>}
                                <span className="text-xs text-muted-foreground ml-auto">
                                  {c.created_at ? new Date(c.created_at).toLocaleDateString('en-US', {
                                    month: 'short', day: 'numeric'
                                  }) : ''}
                                </span>
                              </div>
                              {hasComment ? (
                                <p className="text-sm text-foreground">{c.comment}</p>
                              ) : !hasReason ? (
                                <p className="text-sm text-muted-foreground italic">No feedback provided</p>
                              ) : null}
                              <p className="text-xs text-muted-foreground mt-1">Ticket #{c.ticket_id}</p>
                            </div>
                          )
                        })}
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground py-4 text-center">No negative ratings in this period</p>
                    )}
                  </CardContent>
                </Card>

                {/* Good Feedback */}
                <Card className="border-green-200 dark:border-green-900/50">
                  <CardHeader>
                    <div className="flex items-center gap-2">
                      <Badge variant="default">Good</Badge>
                      <CardTitle className="text-base">Positive Feedback</CardTitle>
                    </div>
                    <CardDescription>
                      {goodComments.length} rated good with feedback
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {goodComments.length > 0 ? (
                      <div className="space-y-3 max-h-96 overflow-y-auto">
                        {goodComments.map((c, i) => {
                          const hasReason = c.reason && c.reason !== 'No reason provided'
                          return (
                            <div key={i} className="border border-green-200 dark:border-green-900/40 bg-green-50/50 dark:bg-green-950/20 rounded-lg p-3">
                              <div className="flex items-center justify-between mb-1.5">
                                {hasReason && <span className="text-xs font-medium text-green-700 dark:text-green-400">{c.reason}</span>}
                                <span className="text-xs text-muted-foreground ml-auto">
                                  {c.created_at ? new Date(c.created_at).toLocaleDateString('en-US', {
                                    month: 'short', day: 'numeric'
                                  }) : ''}
                                </span>
                              </div>
                              {c.comment && (
                                <p className="text-sm text-foreground">{c.comment}</p>
                              )}
                              <p className="text-xs text-muted-foreground mt-1">Ticket #{c.ticket_id}</p>
                            </div>
                          )
                        })}
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground py-4 text-center">No positive feedback in this period</p>
                    )}
                  </CardContent>
                </Card>
              </div>
            )
          })()}

          {/* Rep Performance - per-assignee response/resolution metrics */}
          <RepPerformance dateRange={dateRange} />
        </>
      )}
    </div>
  )
}
